import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { X, BookOpen, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { METRIC_TOOLTIPS } from "../MetricTooltip/metricCopy";
import "./GlossaryDrawer.css";

/**
 * GlossaryDrawerProvider. Wraps the app so any nested component (usually a
 * MetricTooltip trigger) can call `useGlossaryDrawer().open(metricKey)` to
 * slide open a right-side drawer that shows the full glossary entry.
 *
 * The drawer is dismissible with the close button, the Escape key, or by
 * clicking the backdrop. Focus is trapped inside while open.
 */

const GlossaryDrawerContext = createContext(null);

function keyToLabel(key) {
  const stripped = String(key).replace(/^(fo|ops|crm|hr|bank|data|bs|sales|exp|cc)_/, "");
  const spaced = stripped
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function GlossaryDrawerProvider({ children }) {
  const [openKey, setOpenKey] = useState(null);
  const navigate = useNavigate();

  const open = useCallback((metricKey) => {
    if (metricKey && METRIC_TOOLTIPS[metricKey]) setOpenKey(metricKey);
  }, []);
  const close = useCallback(() => setOpenKey(null), []);

  useEffect(() => {
    if (!openKey) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openKey, close]);

  const entry = openKey ? METRIC_TOOLTIPS[openKey] : null;

  return (
    <GlossaryDrawerContext.Provider value={{ open, close, openKey }}>
      {children}
      {openKey && entry && (
        <div className="gd-overlay" onClick={close} data-testid="glossary-drawer-overlay">
          <aside
            className="gd-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="gd-title"
            data-testid="glossary-drawer"
          >
            <header className="gd-header">
              <div className="gd-header-title">
                <BookOpen size={18} style={{ color: "#1B75BA" }} />
                <div>
                  <div className="gd-eyebrow">Metric Glossary</div>
                  <h2 id="gd-title" className="gd-title" data-testid="glossary-drawer-title">{keyToLabel(openKey)}</h2>
                  <div className="gd-key" data-testid="glossary-drawer-key">{openKey}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                className="gd-close"
                aria-label="Close glossary drawer"
                data-testid="glossary-drawer-close"
              >
                <X size={16} />
              </button>
            </header>

            <div className="gd-body">
              <section className="gd-section">
                <div className="gd-section-label">WHAT IT IS</div>
                <p className="gd-text" data-testid="glossary-drawer-info">{entry.info}</p>
              </section>

              <section className="gd-section">
                <div className="gd-section-label">HOW TO INTERPRET</div>
                <p className="gd-text" data-testid="glossary-drawer-help">{entry.help}</p>
              </section>

              {entry.accountingNote && (
                <section className="gd-note">
                  Reflects the accounting method (cash or accrual) and date range selected in settings.
                </section>
              )}
            </div>

            <footer className="gd-footer">
              <button
                type="button"
                onClick={() => { close(); navigate("/glossary"); }}
                className="gd-open-full"
                data-testid="glossary-drawer-open-full"
              >
                <ExternalLink size={13} />
                Open full Glossary
              </button>
            </footer>
          </aside>
        </div>
      )}
    </GlossaryDrawerContext.Provider>
  );
}

/**
 * Hook. `open(metricKey)` and `close()` for the drawer.
 * Safe to call from anywhere below GlossaryDrawerProvider.
 */
export function useGlossaryDrawer() {
  const ctx = useContext(GlossaryDrawerContext);
  return ctx || { open: () => {}, close: () => {}, openKey: null };
}
